import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography, Box, Paper, Alert, CircularProgress, LinearProgress,
  List, ListItemButton, ListItemIcon, ListItemText, Collapse, IconButton,
  Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Divider, Chip, InputAdornment,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import Layout from '../../components/Layout';
import fileManagerService from '../../services/fileManagerService';
import { useUnion } from '../../contexts/UnionContext';

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function HighlightedText({ text, query }) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ backgroundColor: '#fff176', borderRadius: 2, padding: '0 1px' }}>
        {text.slice(idx, idx + query.length)}
      </span>
      {text.slice(idx + query.length)}
    </>
  );
}

function filterTree(folders, query) {
  if (!query) return folders;
  const q = query.toLowerCase();

  function folderMatches(folder) {
    const nameMatch = folder.name.toLowerCase().includes(q);
    const fileMatches = (folder.files || []).filter((f) => f.name.toLowerCase().includes(q));
    const childResults = (folder.children || []).map(folderMatches).filter(Boolean);

    if (!nameMatch && fileMatches.length === 0 && childResults.length === 0) return null;

    return {
      ...folder,
      files: nameMatch ? folder.files : fileMatches,
      children: nameMatch ? folder.children : childResults,
    };
  }

  return folders.map(folderMatches).filter(Boolean);
}

function FileNode({ file, onContextMenu, searchQuery }) {
  return (
    <ListItemButton
      dense
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ type: 'file', id: file.id }));
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onContextMenu(e, { type: 'file', item: file });
      }}
      sx={{ pl: 4 }}
    >
      <ListItemIcon sx={{ minWidth: 32 }}>
        <InsertDriveFileIcon fontSize="small" color="action" />
      </ListItemIcon>
      <ListItemText
        primary={<HighlightedText text={file.name} query={searchQuery} />}
        secondary={formatBytes(file.file_size)}
        primaryTypographyProps={{ variant: 'body2' }}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
    </ListItemButton>
  );
}

function FolderNode({ folder, depth, expanded, onToggle, onContextMenu, onDrop, onFileDrop, searchQuery }) {
  const isOpen = expanded[folder.id];
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    // OS file drop
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const hasJson = e.dataTransfer.types.includes('application/json');
      if (!hasJson) {
        onFileDrop(folder.id, Array.from(e.dataTransfer.files));
        return;
      }
    }

    // Internal drag
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      onDrop(folder.id, data);
    } catch { /* ignore */ }
  };

  return (
    <Box sx={{ ml: depth > 0 ? 2 : 0 }}>
      <ListItemButton
        dense
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData('application/json', JSON.stringify({ type: 'folder', id: folder.id }));
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => onToggle(folder.id)}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onContextMenu(e, { type: 'folder', item: folder });
        }}
        sx={{
          bgcolor: dragOver ? 'action.hover' : 'transparent',
          border: dragOver ? '2px dashed' : '2px solid transparent',
          borderColor: dragOver ? 'primary.main' : 'transparent',
          borderRadius: 1,
        }}
      >
        <ListItemIcon sx={{ minWidth: 28 }}>
          {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </ListItemIcon>
        <ListItemIcon sx={{ minWidth: 28 }}>
          {isOpen ? <FolderOpenIcon fontSize="small" color="primary" /> : <FolderIcon fontSize="small" color="primary" />}
        </ListItemIcon>
        <ListItemText
          primary={<HighlightedText text={folder.name} query={searchQuery} />}
          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
        />
        {folder.files?.length > 0 && (
          <Chip label={folder.files.length} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
        )}
      </ListItemButton>

      <Collapse in={isOpen || !!searchQuery} timeout="auto">
        {folder.children?.map((child) => (
          <FolderNode
            key={child.id} folder={child} depth={depth + 1}
            expanded={expanded} onToggle={onToggle}
            onContextMenu={onContextMenu} onDrop={onDrop} onFileDrop={onFileDrop}
            searchQuery={searchQuery}
          />
        ))}
        {folder.files?.map((f) => (
          <Box key={f.id} sx={{ ml: depth > 0 ? 2 : 0 }}>
            <FileNode file={f} onContextMenu={onContextMenu} searchQuery={searchQuery} />
          </Box>
        ))}
      </Collapse>
    </Box>
  );
}

function FileManager() {
  const { t } = useTranslation();
  const { activeUnion } = useUnion();
  const [folders, setFolders] = useState([]);
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});

  // Context menu
  const [contextMenu, setContextMenu] = useState(null); // { mouseX, mouseY, target }

  // Dialogs
  const [dialog, setDialog] = useState(null); // { type: 'createFolder'|'rename'|'delete', target }
  const [dialogInput, setDialogInput] = useState('');
  const [dialogLoading, setDialogLoading] = useState(false);

  // Upload
  const [uploading, setUploading] = useState([]); // [{name, progress}]
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // OS drag overlay
  const [osDragOver, setOsDragOver] = useState(false);

  const loadData = useCallback(async () => {
    if (!activeUnion) return;
    setLoading(true);
    try {
      const [foldersData, storageData] = await Promise.all([
        fileManagerService.getFolders(),
        fileManagerService.getStorageUsage(),
      ]);
      setFolders(Array.isArray(foldersData) ? foldersData : foldersData.results || []);
      setStorage(storageData);
    } catch {
      setError(t('fileManager.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [activeUnion, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleToggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleContextMenu = (e, target) => {
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, target });
  };

  const closeContextMenu = () => setContextMenu(null);

  // --- Actions ---

  const handleCreateFolder = async () => {
    if (!dialogInput.trim()) return;
    setDialogLoading(true);
    try {
      const parentId = dialog.target?.item?.id || null;
      await fileManagerService.createFolder({ name: dialogInput.trim(), parent: parentId });
      if (parentId) setExpanded((prev) => ({ ...prev, [parentId]: true }));
      setDialog(null);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.name?.[0] || t('fileManager.failedCreateFolder'));
    } finally {
      setDialogLoading(false);
    }
  };

  const handleRename = async () => {
    if (!dialogInput.trim()) return;
    setDialogLoading(true);
    try {
      const { type, item } = dialog.target;
      if (type === 'folder') {
        await fileManagerService.updateFolder(item.id, { name: dialogInput.trim() });
      } else {
        await fileManagerService.renameFile(item.id, dialogInput.trim());
      }
      setDialog(null);
      await loadData();
    } catch {
      setError(t('fileManager.failedRename'));
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async () => {
    setDialogLoading(true);
    try {
      const { type, item } = dialog.target;
      if (type === 'folder') {
        await fileManagerService.deleteFolder(item.id);
      } else {
        await fileManagerService.deleteFile(item.id);
      }
      setDialog(null);
      await loadData();
    } catch {
      setError(t('fileManager.failedDelete'));
    } finally {
      setDialogLoading(false);
    }
  };

  const uploadFiles = async (folderId, files) => {
    const uploads = files.map((f) => ({ name: f.name, progress: 0 }));
    setUploading((prev) => [...prev, ...uploads]);

    for (let i = 0; i < files.length; i++) {
      try {
        await fileManagerService.uploadFile(folderId, files[i], (e) => {
          const pct = Math.round((e.loaded / e.total) * 100);
          setUploading((prev) => {
            const copy = [...prev];
            const idx = copy.findIndex((u) => u.name === files[i].name);
            if (idx >= 0) copy[idx] = { ...copy[idx], progress: pct };
            return copy;
          });
        });
      } catch (err) {
        setError(t('fileManager.uploadFailed', { filename: files[i].name, error: err.response?.data?.file?.[0] || err.message }));
      }
    }

    setUploading((prev) => prev.filter((u) => !files.some((f) => f.name === u.name)));
    setExpanded((prev) => ({ ...prev, [folderId]: true }));
    await loadData();
  };

  const handleUploadClick = (folderId) => {
    uploadTargetRef.current = folderId;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0 && uploadTargetRef.current) {
      uploadFiles(uploadTargetRef.current, files);
    }
    e.target.value = '';
  };

  const handleInternalDrop = async (targetFolderId, data) => {
    try {
      if (data.type === 'file') {
        await fileManagerService.moveFile(data.id, targetFolderId);
      } else if (data.type === 'folder' && data.id !== targetFolderId) {
        await fileManagerService.reorderFolders([{ id: data.id, parent: targetFolderId, sort_order: 0 }]);
      }
      await loadData();
    } catch {
      setError(t('fileManager.failedMove'));
    }
  };

  const handleOsFileDrop = (folderId, files) => {
    uploadFiles(folderId, files);
  };

  // Root-level OS drag-and-drop
  const handleRootDragOver = (e) => {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      setOsDragOver(true);
    }
  };
  const handleRootDragLeave = () => setOsDragOver(false);
  const handleRootDrop = (e) => {
    e.preventDefault();
    setOsDragOver(false);
    if (e.dataTransfer.files.length > 0 && folders.length > 0) {
      uploadFiles(folders[0].id, Array.from(e.dataTransfer.files));
    }
  };

  const storageColor = storage?.used_percent > 90 ? 'error' : storage?.used_percent > 75 ? 'warning' : 'primary';

  return (
    <Layout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">{t('fileManager.title')}</Typography>
        <Button
          variant="outlined" startIcon={<CreateNewFolderIcon />}
          onClick={() => { setDialog({ type: 'createFolder', target: null }); setDialogInput(''); }}
        >
          {t('fileManager.newRootFolder')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Storage usage bar */}
      {storage && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">{t('fileManager.storage')}</Typography>
            <Typography variant="body2">
              {formatBytes(storage.used_bytes)} / {formatBytes(storage.quota_bytes)} ({storage.used_percent}%)
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={Math.min(storage.used_percent, 100)} color={storageColor} sx={{ height: 8, borderRadius: 1 }} />
        </Paper>
      )}

      {/* Search */}
      <TextField
        fullWidth size="small" placeholder={t('fileManager.searchPlaceholder')}
        value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>
          ),
          sx: { bgcolor: 'background.paper' },
        }}
      />

      {/* Upload progress */}
      {uploading.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          {uploading.map((u, i) => (
            <Box key={i} sx={{ mb: 1 }}>
              <Typography variant="caption">{u.name}</Typography>
              <LinearProgress variant="determinate" value={u.progress} sx={{ height: 4, borderRadius: 1 }} />
            </Box>
          ))}
        </Paper>
      )}

      {/* File tree */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          sx={{
            minHeight: 300,
            border: osDragOver ? '2px dashed' : '1px solid',
            borderColor: osDragOver ? 'primary.main' : 'divider',
            bgcolor: osDragOver ? 'action.hover' : 'background.paper',
            position: 'relative',
          }}
          onDragOver={handleRootDragOver}
          onDragLeave={handleRootDragLeave}
          onDrop={handleRootDrop}
          onContextMenu={(e) => {
            e.preventDefault();
            handleContextMenu(e, { type: 'root', item: null });
          }}
        >
          {osDragOver && (
            <Box sx={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(25,118,210,0.08)', zIndex: 10, pointerEvents: 'none',
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                <Typography color="primary">{t('fileManager.dropToUpload')}</Typography>
              </Box>
            </Box>
          )}

          {filterTree(folders, searchQuery).length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {searchQuery ? t('fileManager.noMatches') : t('fileManager.noFolders')}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding sx={{ py: 1 }}>
              {filterTree(folders, searchQuery).map((folder) => (
                <FolderNode
                  key={folder.id} folder={folder} depth={0}
                  expanded={expanded} onToggle={handleToggle}
                  onContextMenu={handleContextMenu}
                  onDrop={handleInternalDrop}
                  onFileDrop={handleOsFileDrop}
                  searchQuery={searchQuery}
                />
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* Hidden file input */}
      <input type="file" multiple hidden ref={fileInputRef} onChange={handleFileInputChange} />

      {/* Context Menu */}
      <Menu
        open={!!contextMenu}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        {contextMenu?.target?.type === 'folder' && [
          <MenuItem key="subfolder" onClick={() => {
            setDialog({ type: 'createFolder', target: contextMenu.target });
            setDialogInput('');
            closeContextMenu();
          }}>
            <ListItemIcon><CreateNewFolderIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('fileManager.newSubfolder')}</ListItemText>
          </MenuItem>,
          <MenuItem key="upload" onClick={() => {
            handleUploadClick(contextMenu.target.item.id);
            closeContextMenu();
          }}>
            <ListItemIcon><UploadFileIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('fileManager.uploadFiles')}</ListItemText>
          </MenuItem>,
          <Divider key="d1" />,
          <MenuItem key="rename" onClick={() => {
            setDialog({ type: 'rename', target: contextMenu.target });
            setDialogInput(contextMenu.target.item.name);
            closeContextMenu();
          }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('common.rename')}</ListItemText>
          </MenuItem>,
          <MenuItem key="delete" onClick={() => {
            setDialog({ type: 'delete', target: contextMenu.target });
            closeContextMenu();
          }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>{t('fileManager.deleteFolder')}</ListItemText>
          </MenuItem>,
        ]}
        {contextMenu?.target?.type === 'file' && [
          <MenuItem key="download" onClick={() => {
            window.open(contextMenu.target.item.download_url, '_blank');
            closeContextMenu();
          }}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('common.download')}</ListItemText>
          </MenuItem>,
          <MenuItem key="rename" onClick={() => {
            setDialog({ type: 'rename', target: contextMenu.target });
            setDialogInput(contextMenu.target.item.name);
            closeContextMenu();
          }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('common.rename')}</ListItemText>
          </MenuItem>,
          <MenuItem key="delete" onClick={() => {
            setDialog({ type: 'delete', target: contextMenu.target });
            closeContextMenu();
          }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>{t('fileManager.deleteFile')}</ListItemText>
          </MenuItem>,
        ]}
        {contextMenu?.target?.type === 'root' && [
          <MenuItem key="newfolder" onClick={() => {
            setDialog({ type: 'createFolder', target: null });
            setDialogInput('');
            closeContextMenu();
          }}>
            <ListItemIcon><CreateNewFolderIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('fileManager.newFolder')}</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      {/* Create Folder Dialog */}
      <Dialog open={dialog?.type === 'createFolder'} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('fileManager.newFolder')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label={t('fileManager.folderName')} value={dialogInput}
            onChange={(e) => setDialogInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreateFolder} disabled={dialogLoading || !dialogInput.trim()}>
            {dialogLoading ? <CircularProgress size={20} /> : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={dialog?.type === 'rename'} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{dialog?.target?.type === 'folder' ? t('fileManager.renameFolder') : t('fileManager.renameFile')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label={t('common.name')} value={dialogInput}
            onChange={(e) => setDialogInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleRename} disabled={dialogLoading || !dialogInput.trim()}>
            {dialogLoading ? <CircularProgress size={20} /> : t('common.rename')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={dialog?.type === 'delete'} onClose={() => setDialog(null)}>
        <DialogTitle>{dialog?.target?.type === 'folder' ? t('fileManager.deleteFolder') : t('fileManager.deleteFile')}?</DialogTitle>
        <DialogContent>
          <Typography>
            {dialog?.target?.type === 'folder'
              ? t('fileManager.deleteFolderMsg')
              : `${t('common.delete')} "${dialog?.target?.item?.name}"?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={dialogLoading}>
            {dialogLoading ? <CircularProgress size={20} /> : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default FileManager;
